import { useState, useRef, useEffect, useCallback } from 'react';
import { Wand2, CheckCircle2, Loader2, RotateCcw, FileText, MessageSquareText, Package, Calendar, AlertCircle, ChevronDown, ChevronUp, HelpCircle, ArrowRight, SkipForward } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  streamGeneratePromptV2,
  streamGenerateCalendarPrompt,
  analyzeBusinessContext,
  GenerateV2Context,
  GenerateV2Analysis,
  GenerateV2Validation,
  GenerateV2Stats,
  ClarifyingQuestion,
} from '../../../../channels/services/promptGenerationService';
import { BriefDocumentUpload } from '../../../../channels/components/BriefDocumentUpload';
import type { DocumentUpload } from '../../../../channels/types/onboarding';

interface AutoGeneratePromptProps {
  onApplyPrompt: (prompt: string) => void;
  onApplyCalendarPrompt?: (prompt: string) => void;
  onFlowComplete?: () => void;
  onUpdateContext?: (field: 'documentos_negocio', value: string) => void;
  context: GenerateV2Context;
  onBack?: () => void;
  agentId?: string;
}

type Phase = 'idle' | 'analyzing' | 'questions' | 'generating' | 'validating' | 'done' | 'calendar_generating' | 'calendar_done' | 'error';

export function AutoGeneratePrompt({ onApplyPrompt, onApplyCalendarPrompt, onFlowComplete, onUpdateContext, context, onBack, agentId }: AutoGeneratePromptProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [phaseMessage, setPhaseMessage] = useState('');
  const [analysis, setAnalysis] = useState<GenerateV2Analysis | null>(null);
  const [questions, setQuestions] = useState<ClarifyingQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [needsCalendar, setNeedsCalendar] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [validation, setValidation] = useState<GenerateV2Validation | null>(null);
  const [stats, setStats] = useState<GenerateV2Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAnalysisDetails, setShowAnalysisDetails] = useState(false);

  // Calendar generation state
  const [calendarPromptText, setCalendarPromptText] = useState('');
  const [showCalendarPreview, setShowCalendarPreview] = useState(false);
  const [calendarApplied, setCalendarApplied] = useState(false);
  const [calendarAnswers, setCalendarAnswers] = useState<Record<string, string>>({});
  const [briefDocuments, setBriefDocuments] = useState<DocumentUpload[]>([]);

  const abortRef = useRef<(() => void) | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const calendarAbortRef = useRef<(() => void) | null>(null);
  const previewRef = useRef<HTMLPreElement>(null);
  const calendarPreviewRef = useRef<HTMLPreElement>(null);

  // Auto-scroll preview during streaming
  useEffect(() => {
    if (phase === 'generating' && previewRef.current) {
      previewRef.current.scrollTop = previewRef.current.scrollHeight;
    }
  }, [promptText, phase]);

  useEffect(() => {
    if (phase === 'calendar_generating' && calendarPreviewRef.current) {
      calendarPreviewRef.current.scrollTop = calendarPreviewRef.current.scrollHeight;
    }
  }, [calendarPromptText, phase]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current();
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (calendarAbortRef.current) calendarAbortRef.current();
    };
  }, []);

  const contextInfo = [
    { key: 'historial', label: 'Historial de chats', icon: MessageSquareText, chars: context.historial_conversaciones?.length || 0 },
    { key: 'docs', label: 'Documentos del negocio', icon: FileText, chars: context.documentos_negocio?.length || 0 },
    { key: 'productos', label: 'Productos', icon: Package, chars: context.productos?.length || 0 },
    { key: 'calendario', label: 'Prompt de calendario', icon: Calendar, chars: context.prompt_calendario?.length || 0 },
  ].filter(item => item.chars > 0);

  const hasContext = contextInfo.length > 0;

  const handleGenerate = useCallback(async () => {
    if (abortRef.current) abortRef.current();
    if (abortControllerRef.current) abortControllerRef.current.abort();

    setPhase('analyzing');
    setPhaseMessage('Analizando datos del negocio, historial y documentos...');
    setAnalysis(null);
    setQuestions([]);
    setAnswers({});
    setNeedsCalendar(false);
    setPromptText('');
    setValidation(null);
    setStats(null);
    setError(null);
    setApplied(false);
    setShowPreview(false);
    setShowAnalysisDetails(false);
    setCalendarPromptText('');
    setCalendarApplied(false);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const result = await analyzeBusinessContext(context, agentId, controller.signal);
      setAnalysis(result.analysis);

      // Detect calendar from analysis
      const calendarDetected = result.analysis.calendar?.uses_calendar === true;
      setNeedsCalendar(calendarDetected);

      if (result.has_questions && result.questions.length > 0) {
        setQuestions(result.questions);
        // Pre-fill suggested answers + calendar toggle
        const initial: Record<string, string> = {};
        for (const q of result.questions) {
          if (q.field_path === 'calendar.uses_calendar') {
            initial[q.id] = calendarDetected ? 'Sí' : 'No';
          } else if (q.suggested_answer) {
            initial[q.id] = q.suggested_answer;
          }
        }
        setAnswers(initial);
        setPhase('questions');
        setPhaseMessage('');
      } else {
        startGeneration(result.analysis, []);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Error en el análisis');
      setPhase('error');
    }
  }, [context, agentId]);

  const startGeneration = useCallback((analysisData: GenerateV2Analysis, userAnswers: Array<{ question_id: string; answer: string }>) => {
    setPhase('generating');
    setPhaseMessage(userAnswers.length
      ? `Generando prompt con ${userAnswers.length} aclaraciones del usuario...`
      : 'Generando prompt completo con IA...');
    setShowPreview(true);
    setPromptText('');

    const cleanup = streamGeneratePromptV2(context, {
      onPhase: (p, message) => {
        if (p === 'generating') setPhase('generating');
        else if (p === 'validating') setPhase('validating');
        setPhaseMessage(message);
      },
      onAnalysis: () => {},
      onText: (text) => {
        setPromptText(prev => prev + text);
      },
      onValidation: (data) => {
        setValidation(data);
      },
      onDone: (s) => {
        setStats(s);
        setPhase('done');
      },
      onError: (err) => {
        setError(err);
        setPhase('error');
      },
    }, {
      analysis: analysisData,
      userAnswers: userAnswers.length > 0 ? userAnswers : undefined,
      agentId,
    });

    abortRef.current = cleanup;
  }, [context, agentId]);

  const handleContinueWithAnswers = useCallback(() => {
    if (!analysis) return;
    const userAnswers = Object.entries(answers)
      .filter(([, v]) => v.trim())
      .map(([qId, answer]) => ({ question_id: qId, answer }));

    // Check calendar toggle answer
    const calendarQ = questions.find(q => q.field_path === 'calendar.uses_calendar');
    if (calendarQ) {
      const calAnswer = answers[calendarQ.id];
      const wantsCalendar = /s[ií]|yes/i.test(calAnswer || '');
      setNeedsCalendar(wantsCalendar);
    }

    // Extract calendar-specific answers for later use in calendar prompt generation
    const calAnswers: Record<string, string> = {};
    for (const q of questions) {
      if (q.field_path.startsWith('calendar.') && q.field_path !== 'calendar.uses_calendar' && answers[q.id]?.trim()) {
        calAnswers[q.id] = answers[q.id];
      }
    }
    setCalendarAnswers(calAnswers);

    startGeneration(analysis, userAnswers);
  }, [analysis, answers, questions, startGeneration]);

  const handleSkipQuestions = useCallback(() => {
    if (!analysis) return;
    startGeneration(analysis, []);
  }, [analysis, startGeneration]);

  const handleApply = () => {
    if (promptText.trim()) {
      onApplyPrompt(promptText.trim());
      setApplied(true);
      if (needsCalendar && onApplyCalendarPrompt) {
        // Auto-start calendar generation
        setPhase('calendar_generating');
        setTimeout(() => handleGenerateCalendar(), 300);
      } else {
        onFlowComplete?.();
      }
    }
  };

  const handleGenerateCalendar = useCallback(() => {
    if (!analysis) return;
    setPhase('calendar_generating');
    setCalendarPromptText('');
    setShowCalendarPreview(true);

    const cleanup = streamGenerateCalendarPrompt(analysis, {
      onText: (text) => {
        setCalendarPromptText(prev => prev + text);
      },
      onDone: () => {
        setPhase('calendar_done');
      },
      onError: (err) => {
        setPhase('done'); // fallback to done state
        setError(err);
      },
    }, agentId, calendarAnswers);

    calendarAbortRef.current = cleanup;
  }, [analysis, agentId, calendarAnswers]);

  const handleApplyAll = () => {
    // Apply calendar prompt and close the flow
    if (calendarPromptText.trim() && onApplyCalendarPrompt) {
      onApplyCalendarPrompt(calendarPromptText.trim());
      setCalendarApplied(true);
    }
    onFlowComplete?.();
  };

  const handleSkipCalendar = () => {
    onFlowComplete?.();
  };

  const handleCancel = () => {
    if (abortRef.current) abortRef.current();
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setPhase('error');
    setError('Cancelado por el usuario');
  };

  const formatChars = (chars: number): string => {
    if (chars >= 1000000) return `${(chars / 1000000).toFixed(1)}M`;
    if (chars >= 1000) return `${(chars / 1000).toFixed(0)}K`;
    return `${chars}`;
  };

  const formatTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  const getStepIndex = (): number => {
    switch (phase) {
      case 'analyzing': return 0;
      case 'questions': return 1;
      case 'generating': return 2;
      case 'validating': return 3;
      case 'done': return 4;
      case 'calendar_generating': return 5;
      case 'calendar_done': return 6;
      case 'error': return 4;
      default: return -1;
    }
  };

  const stepLabels = needsCalendar
    ? ['Análisis', 'Preguntas', 'Generación', 'Validación', 'Calendario']
    : ['Análisis', 'Preguntas', 'Generación', 'Validación'];

  const phaseIndex = getStepIndex();

  const getQuestionTypeBadge = (type: ClarifyingQuestion['type']) => {
    switch (type) {
      case 'inconsistency': return { bg: 'bg-red-50 text-red-500 border border-red-200', label: 'Inconsistencia' };
      case 'missing_info': return { bg: 'bg-muted text-muted-foreground border border-border', label: 'Info faltante' };
      case 'strategic': return { bg: 'bg-primary/5 text-primary border border-primary/20', label: 'Estratégica' };
      default: return { bg: 'bg-muted text-muted-foreground border border-border', label: 'Ambigüedad' };
    }
  };

  return (
    <div className="bg-background overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Generador Automático</span>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Phase Stepper — dynamic steps */}
        {phase !== 'idle' && (
          <div className="flex items-center gap-1">
            {stepLabels.map((label, i) => {
              const isActive = phaseIndex === i;
              const isDone = phaseIndex > i;
              const isQuestions = i === 1 && phase === 'questions';
              const isCalendarStep = label === 'Calendario';
              return (
                <div key={label} className="flex items-center gap-1 flex-1">
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all ${
                    isDone ? 'bg-emerald-100 text-emerald-700' :
                    isActive ? (isCalendarStep ? 'bg-primary/10 text-primary' : 'bg-primary/10 text-primary') :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {isDone ? (
                      <CheckCircle2 className="w-3 h-3" />
                    ) : isActive ? (
                      isQuestions ? <HelpCircle className="w-3 h-3" /> : <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <span className="w-3 h-3 rounded-full border border-current inline-block" />
                    )}
                    <span className="hidden sm:inline">{label}</span>
                  </div>
                  {i < stepLabels.length - 1 && <div className={`flex-1 h-px ${isDone ? 'bg-emerald-300' : 'bg-border'}`} />}
                </div>
              );
            })}
          </div>
        )}

        {/* Phase Message */}
        {(phase === 'analyzing' || phase === 'generating' || phase === 'validating' || phase === 'calendar_generating') && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>{phase === 'calendar_generating' ? 'Generando prompt de sub-agente de calendario...' : phaseMessage}</span>
          </div>
        )}

        {/* Context Available (idle state) */}
        {phase === 'idle' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Genera automáticamente un prompt profesional de 600+ líneas usando los datos del onboarding.
            </p>
            {hasContext ? (
              <div className="space-y-1">
                <span className="text-xs font-medium text-foreground/70">Contexto disponible:</span>
                {contextInfo.map(item => (
                  <div key={item.key} className="flex items-center gap-2 text-xs text-emerald-600">
                    <item.icon className="w-3 h-3" />
                    <span>{item.label}: {formatChars(item.chars)} chars</span>
                  </div>
                ))}
              </div>
            ) : (
              /* No context — show brief document upload */
              <div className="space-y-3">
                <BriefDocumentUpload
                  documents={briefDocuments}
                  onChange={setBriefDocuments}
                />
                {(() => {
                  const doneDocs = briefDocuments.filter(d => d.status === 'done');
                  const isProcessing = briefDocuments.some(d => d.status === 'uploading' || d.status === 'processing');
                  if (doneDocs.length === 0) return null;
                  return (
                    <Button
                      onClick={() => {
                        const allText = doneDocs
                          .map(d => `--- ${d.fileName} ---\n${d.extractedText || d.summary || ''}`)
                          .join('\n\n');
                        if (allText.trim()) {
                          onUpdateContext?.('documentos_negocio', allText.trim());
                        }
                      }}
                      disabled={isProcessing}
                      className="w-full"
                      size="sm"
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Continuar con {doneDocs.length} documento{doneDocs.length > 1 ? 's' : ''}
                    </Button>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Analysis Summary */}
        {analysis && phase !== 'idle' && (
          <div className="p-2 bg-muted/30 rounded-md border border-border">
            <div className="text-xs font-medium text-foreground/70 mb-1">Negocio detectado:</div>
            <div className="text-sm font-semibold text-foreground">{analysis.business?.name || 'Sin nombre'}</div>
            <div className="text-xs text-muted-foreground">{analysis.business?.sector} — {analysis.business?.city}</div>
            {analysis.products_services?.length > 0 && (
              <div className="text-xs text-muted-foreground mt-1">
                {analysis.products_services.length} servicio(s) detectado(s)
              </div>
            )}

            {/* Expandable Analysis Details (Mejora 2) */}
            <button
              onClick={() => setShowAnalysisDetails(!showAnalysisDetails)}
              className="flex items-center gap-1 text-[10px] text-primary mt-2 hover:underline"
            >
              {showAnalysisDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showAnalysisDetails ? 'Ocultar detalles' : 'Ver detalles del análisis'}
            </button>

            {showAnalysisDetails && (
              <div className="mt-2 space-y-2 text-[10px] text-foreground/70 border-t border-border pt-2">
                {/* Products & Prices */}
                {analysis.products_services?.length > 0 && (
                  <div>
                    <span className="font-semibold">Servicios/Productos:</span>
                    <ul className="list-disc pl-3 mt-0.5">
                      {analysis.products_services.slice(0, 8).map((p: any, i: number) => (
                        <li key={i}>{p.name} — {p.price || 'Sin precio'}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Top Objections */}
                {analysis.sales_patterns?.top_objections?.length > 0 && (
                  <div>
                    <span className="font-semibold">Objeciones detectadas:</span>
                    <ul className="list-disc pl-3 mt-0.5">
                      {analysis.sales_patterns.top_objections.slice(0, 5).map((o: any, i: number) => (
                        <li key={i}>"{o.objection}"</li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Communication */}
                {analysis.communication && (
                  <div>
                    <span className="font-semibold">Comunicación:</span>
                    <span> Tono: {analysis.communication.tone || '?'}, {analysis.communication.language_formality || '?'}, Emojis: {analysis.communication.emoji_usage || '?'}</span>
                  </div>
                )}
                {/* Calendar */}
                <div>
                  <span className="font-semibold">Calendario:</span>
                  <span> {analysis.calendar?.uses_calendar ? 'Sí usa calendario' : 'No usa calendario'}</span>
                </div>
                {/* Context Quality */}
                {analysis.context_quality && (
                  <div>
                    <span className="font-semibold">Calidad del contexto:</span>
                    <span> Historial: {analysis.context_quality.historial_richness}, Docs: {analysis.context_quality.documents_richness}</span>
                    {analysis.context_quality.gaps?.length > 0 && (
                      <div className="mt-0.5 text-amber-600">Gaps: {analysis.context_quality.gaps.join(', ')}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Questions Phase */}
        {phase === 'questions' && questions.length > 0 && (
          <div className="space-y-3">
            <div className="p-3 bg-background border border-border rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold text-foreground">
                  Preguntas de validación ({questions.length})
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Se detectaron posibles inconsistencias o información faltante. Responde para mejorar la calidad del prompt.
              </p>

              <div className="space-y-4">
                {questions.map((q) => {
                  const isCalendarToggle = q.field_path === 'calendar.uses_calendar';
                  const isCalendarFollowUp = q.field_path.startsWith('calendar.') && !isCalendarToggle;
                  const badge = getQuestionTypeBadge(q.type);

                  // Hide calendar follow-up questions when calendar toggle is "No"
                  if (isCalendarFollowUp && !needsCalendar) return null;

                  return (
                    <div key={q.id} className="space-y-1.5 pb-3 border-b border-border last:border-b-0 last:pb-0">
                      <label className="text-xs font-medium text-foreground block">
                        {q.question}
                      </label>
                      {q.context && (
                        <p className="text-[10px] text-muted-foreground italic">{q.context}</p>
                      )}

                      {isCalendarToggle ? (
                        /* Calendar Toggle — Sí/No */
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              const newVal = answers[q.id] === 'Sí' ? 'No' : 'Sí';
                              setAnswers(prev => ({ ...prev, [q.id]: newVal }));
                              setNeedsCalendar(newVal === 'Sí');
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              answers[q.id] === 'Sí' ? 'bg-primary' : 'bg-muted'
                            }`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                              answers[q.id] === 'Sí' ? 'translate-x-[22px]' : 'translate-x-1'
                            }`} />
                          </button>
                          <span className="text-xs text-foreground/70 font-medium">
                            {answers[q.id] === 'Sí' ? 'Sí, necesita citas' : 'No necesita citas'}
                          </span>
                        </div>
                      ) : (
                        /* Regular text input */
                        <input
                          type="text"
                          value={answers[q.id] || ''}
                          onChange={(e) => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                          placeholder={q.suggested_answer || 'Tu respuesta...'}
                          className="w-full px-2.5 py-1.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground/50"
                        />
                      )}

                      <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full ${badge.bg}`}>
                        {badge.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleContinueWithAnswers}
                className="flex-1"
                size="sm"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Continuar con generación
              </Button>
              <Button
                onClick={handleSkipQuestions}
                variant="outline"
                size="sm"
              >
                <SkipForward className="w-4 h-4 mr-1" />
                Omitir
              </Button>
            </div>
          </div>
        )}

        {/* Streaming Preview */}
        {promptText && (
          <div className="space-y-1">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {showPreview ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              Preview ({promptText.split('\n').length} líneas)
            </button>
            {showPreview && (
              <pre
                ref={previewRef}
                className="max-h-[300px] overflow-y-auto p-3 bg-muted/20 border border-border rounded-md text-xs text-foreground/80 whitespace-pre-wrap font-mono leading-relaxed"
              >
                {promptText}
              </pre>
            )}
          </div>
        )}

        {/* Error */}
        {phase === 'error' && error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Validation Results */}
        {validation && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-emerald-800">Resultado</span>
              <span className={`text-lg font-bold ${
                validation.score >= 85 ? 'text-emerald-700' :
                validation.score >= 70 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {validation.score}/100
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs text-emerald-700">
              <div className="text-center">
                <div className="font-bold text-base">{validation.sections_found}</div>
                <div>secciones</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-base">{validation.total_lines}</div>
                <div>líneas</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-base">{formatChars(validation.total_chars)}</div>
                <div>chars</div>
              </div>
            </div>
            {stats?.generation_time_ms && (
              <div className="text-xs text-emerald-600 text-center">
                Generado en {formatTime(stats.generation_time_ms)}
              </div>
            )}
            {validation.suggestions?.length > 0 && (
              <div className="text-xs text-emerald-700 mt-1">
                <span className="font-medium">Sugerencias:</span>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  {validation.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          {phase === 'idle' && (
            <Button
              onClick={handleGenerate}
              disabled={!hasContext}
              className="w-full"
              size="sm"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Generar Prompt Automáticamente
            </Button>
          )}

          {phase === 'done' && !applied && (
            <>
              <Button onClick={handleApply} className="flex-1" size="sm">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Aplicar Prompt
              </Button>
              <Button onClick={handleGenerate} variant="outline" size="sm">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </>
          )}

          {phase === 'done' && applied && !needsCalendar && (
            <div className="w-full flex items-center justify-center gap-2 py-2 text-sm text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />
              <span>Prompt aplicado y guardado</span>
            </div>
          )}

          {phase === 'error' && (
            <Button onClick={handleGenerate} variant="outline" className="w-full" size="sm">
              <RotateCcw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          )}

          {(phase === 'analyzing' || phase === 'generating' || phase === 'validating') && (
            <Button
              onClick={handleCancel}
              variant="outline"
              className="w-full"
              size="sm"
            >
              Cancelar
            </Button>
          )}
        </div>

        {/* ═══ Calendar Step ═══ */}
        {(phase === 'calendar_generating' || phase === 'calendar_done') && (
          <div className="space-y-3">
            {/* Calendar Preview */}
            {calendarPromptText && (
              <div className="space-y-1">
                <button
                  onClick={() => setShowCalendarPreview(!showCalendarPreview)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  {showCalendarPreview ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  <Calendar className="w-3 h-3" />
                  Prompt de Calendario ({calendarPromptText.split('\n').length} líneas)
                </button>
                {showCalendarPreview && (
                  <pre
                    ref={calendarPreviewRef}
                    className="max-h-[250px] overflow-y-auto p-3 bg-muted/20 border border-border rounded-md text-xs text-foreground/80 whitespace-pre-wrap font-mono leading-relaxed"
                  >
                    {calendarPromptText}
                  </pre>
                )}
              </div>
            )}

            {/* Apply both or skip */}
            {phase === 'calendar_done' && !calendarApplied && (
              <div className="flex gap-2">
                <Button onClick={handleApplyAll} className="flex-1" size="sm">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Aplicar Ambos Prompts
                </Button>
                <Button onClick={handleSkipCalendar} variant="outline" size="sm">
                  <SkipForward className="w-4 h-4 mr-1" />
                  Solo System Prompt
                </Button>
              </div>
            )}

            {calendarApplied && (
              <div className="w-full flex items-center justify-center gap-2 py-2 text-sm text-emerald-600">
                <CheckCircle2 className="w-4 h-4" />
                <span>Ambos prompts aplicados y guardados</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
